import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Star, ChevronDown } from 'lucide-react';
import { PHONE_NUMBER, PHONE_TEL_HREF, TESTIMONIALS } from '../../constants/site';
import { scrollToForm } from './SellerHero';

const SELLING_POINTS = [
  'Hyper-local comparable sales, not a Zestimate',
  'Luxury photography, video & global syndication via Swell Realty Co.',
  'A ready pipeline of relocating military & VA-loan buyers',
  'Straight talk on timing, prep and net proceeds',
];

export const SellingInCopy = ({ area }) => (
  <div className="pt-16 space-y-6">
    <h2 className="font-serif text-3xl sm:text-4xl text-[#143160]">Selling in {area.name}</h2>
    <p className="text-neutral-600 leading-relaxed">{area.name} is known for {area.vibe}. Buyers searching {area.region} are specifically looking for homes like yours — and the right pricing, presentation and marketing make the difference between an average sale and a record one.</p>
    <ul className="space-y-3">
      {SELLING_POINTS.map((t) => (
        <li key={t} className="flex items-start gap-3"><span className="mt-2 w-2 h-2 rounded-full bg-gold-500 shrink-0"></span><span className="text-neutral-700">{t}</span></li>
      ))}
    </ul>
    {area.nearby && <p className="text-sm text-neutral-500">Also serving {area.nearby.join(', ')} and all of {area.region}.</p>}
  </div>
);

export const SellerTestimonials = () => (
  <section className="bg-sand-50 py-16">
    <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-3 gap-8">
      {TESTIMONIALS.slice(0, 3).map((t) => (
        <blockquote key={t.name} className="bg-ivory rounded-2xl p-6 border border-[#143160]/10">
          <div className="flex gap-0.5 mb-3">{['s1', 's2', 's3', 's4', 's5'].map((k) => <Star key={k} className="w-4 h-4 fill-gold-500 text-gold-500" />)}</div>
          <p className="text-neutral-700 text-sm leading-relaxed overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical' }}>"{t.text}"</p>
          <footer className="mt-4 text-sm font-bold text-[#143160]">— {t.name} <span className="font-normal text-neutral-500">· {t.badge}</span></footer>
        </blockquote>
      ))}
    </div>
  </section>
);

const Faq = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-[#143160]/10">
      <button type="button" onClick={() => setOpen(!open)} data-testid={`seller-faq-${idx}`} className="w-full flex items-center justify-between py-5 text-left">
        <span className="font-serif text-xl text-[#143160]">{q}</span>
        <ChevronDown className={`w-5 h-5 text-gold-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="pb-5 text-neutral-600 leading-relaxed">{a}</p>}
    </div>
  );
};

export const SellerFaqSection = ({ areaName, faqs }) => (
  <section className="max-w-3xl mx-auto px-5 py-16">
    <h2 className="font-serif text-3xl sm:text-4xl text-[#143160] mb-6">{areaName} Seller FAQs</h2>
    {faqs.map((f, i) => <Faq key={f.q} idx={i} {...f} />)}
  </section>
);

export const MoreSellerGuides = ({ areas }) => (
  <section className="bg-ivory border-t border-[#143160]/10 py-12">
    <div className="max-w-6xl mx-auto px-5">
      <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-bold mb-4">More Seller Guides</p>
      <div className="flex flex-wrap gap-2">
        {areas.map((a) => (
          <Link key={a.slug} to={`/sell/${a.slug}`} data-testid={`seller-link-${a.slug}`} className="px-4 py-2 rounded-full border border-[#143160]/20 text-sm text-[#143160] hover:bg-[#143160] hover:text-white transition-colors">Sell in {a.name}</Link>
        ))}
      </div>
    </div>
  </section>
);

export const SellerFooter = () => (
  <footer className="bg-[#143160] text-white py-10 text-center px-5">
    <p className="font-serif text-2xl sm:text-3xl">Don't stress, <span className="gold-gradient-text font-black">CALL JESS</span> &amp; FORGET THE REST!</p>
    <p className="text-sm text-white/70 mt-3">Jessica Burnette, REALTOR® | Jess Sells East Coast, brokered by Swell Realty Co. | 2122 General Booth Blvd Suite 104, Virginia Beach, VA 23454</p>
    <a href={PHONE_TEL_HREF} className="inline-block mt-4 text-gold-300 font-bold">{PHONE_NUMBER}</a>
  </footer>
);

export const SellerStickyBar = () => (
  <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-[#143160]/95 backdrop-blur border-t border-gold-500/40 p-3 flex gap-2" data-testid="seller-sticky-bar">
    <button type="button" onClick={scrollToForm} className="flex-1 bg-gold-500 text-[#143160] font-black py-3 rounded-md text-sm uppercase">Free Home Value</button>
    <a href={PHONE_TEL_HREF} className="flex-1 border border-white/40 text-white font-bold py-3 rounded-md text-sm text-center flex items-center justify-center"><Phone className="w-4 h-4 mr-1 text-gold-300" />Call Jess</a>
  </div>
);
